import React, { useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { MegaMenu, Footer, ToastProvider, ToastContainer, initTheme, onThemeChange, WxLink } from '@watkinslabs/watkinsx-components';
import './App.css';
import './oxide.css';
import './landing/landing.css';
import './landing/colour.css';
import stats from './generated/stats.json';
import Shell from './landing/Shell';
import Embers from './landing/Embers';
import Ticker from './landing/Ticker';
import Panic from './landing/Panic';
import Downloads from './landing/Downloads';
import { useCountUp, useKonami, useReveal, useScramble, useScrollProgress, useSpotlight, reducedMotion } from './landing/effects';
import Figure, { heatOf } from './landing/Figures';
import { useKernelData } from './landing/data';
import { sysColor, famColor } from './landing/palette';

// Every figure below is generated by `npm run history` from the kernel
// repository itself. Nothing on this page is a hand-typed constant.
const n = (value) => value.toLocaleString('en-US');
const k = (value) => `${Math.round(value / 1000)}K`;

// The valley pulls in three.js and a 2 MB kernel map — code-split so the
// landing page never pays for it.
const Valley = lazy(() => import('./Valley'));
// The growth dashboard carries ~1.5 MB of history data and thirteen charts —
// code-split for the same reason the valley is.
const Growth = lazy(() => import('./growth/Growth.jsx'));

const SOURCE_URL = 'https://gitfoundry.watkinsx.com/watkins-labs/oxide';

// The dashboard needs ~2 MB of history before it can draw. Fetching it while
// the reader is still reading the landing page turns the wait into nothing.
const GROWTH_DATA = ['stats.json', 'history-commits.json', 'history-crates.json'];
let warmed = false;
function warmGrowth() {
  if (warmed) return;
  warmed = true;
  for (const file of GROWTH_DATA) fetch(`/data/${file}`).catch(() => {});
}

// Fonts are injected at runtime because the CI build overwrites index.html
// with the template shell, so a <link> there would not survive.
function useFonts() {
  useEffect(() => {
    const id = 'oxide-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    // Manrope and DM Mono are the valley's typefaces; the rest are the
    // landing's. Audiowide is the wordmark alone.
    link.href = 'https://fonts.googleapis.com/css2?family=Audiowide&family=Chakra+Petch:wght@500;600;700&family=DM+Mono:wght@400;500&family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&family=Manrope:wght@400;600;700;800&display=swap';
    document.head.appendChild(link);
  }, []);
}

// Four cards, not six. The old set said "from scratch", "memory-safe" and
// "vibe-coded" in three different places, and the architecture strip below it
// said the arm64 and driver lines a third time. Each of these now carries one
// idea that appears nowhere else on the page.
// Each card carries a figure drawn from the number it illustrates, rather than
// an icon from a font. A Rust cog over "written in Rust" is the sentence again
// in a smaller typeface; a lattice whose strand count *is* the crate count
// cannot be moved to another card without becoming false. See landing/Figures.
const CAPABILITIES = [
  {
    figure: 'lattice',
    hue: 'rust',
    title: 'Nothing is forked',
    body: `Written in Rust from an empty directory. ${n(stats.rustLoc)} lines, ${n(stats.crates)} crates, memory-safe by construction.`,
  },
  {
    figure: 'syscalls',
    hue: 'quench',
    title: 'It runs Linux software',
    body: 'It implements the Linux ABI, the contract a compiled program expects from a kernel. The syscall table above is live, and it widens every week.',
  },
  {
    figure: 'rails',
    hue: 'arch',
    title: 'Two architectures, one codebase',
    body: 'arm64 and x86_64 are both first-class. Virtualized drivers came first; hardware coverage lands subsystem by subsystem.',
  },
  {
    figure: 'cadence',
    hue: 'ember',
    title: 'AI wrote it. A human aimed it.',
    body: `The fourth iteration, ${stats.days} days of it, and the current limit of what AI-assisted engineering can be pushed to build.`,
  },
];

// Raw numbers, not formatted strings: the strip counts each one up the first
// time it comes into view, and you cannot animate "1,295,631".
//
// The strip has a hierarchy now. Commits lead, because the pace is the story;
// lines of Rust sit beside it with what they are made of; the four counts
// that follow are the supporting cast. Each wears the hue of what it measures.
const LEAD = { value: stats.commits, format: n, label: 'Commits' };
const WIDE = { value: stats.rustLoc, format: k, label: 'Lines of Rust' };
const MINIS = [
  { value: stats.crates,    format: n, label: 'Crates',   hue: 'sys' },
  { value: stats.rustFiles, format: n, label: 'Modules',  hue: 'quench' },
  { value: stats.syscalls,  format: n, label: 'Syscalls', hue: 'fam' },
  { value: stats.tests,     format: n, label: 'Tests',    hue: 'ember' },
];

function Spec({ value, format, label, className = '', children, hue }) {
  const [ref, shown] = useCountUp(value);
  return (
    <div className={`ox-spec ${className}`} ref={ref} data-hue={hue}>
      <div className="ox-spec__value">{format(shown)}</div>
      <div className="ox-spec__label">{label}</div>
      {children}
    </div>
  );
}

// A proportion bar: one segment per row, sized by `share`, coloured by what
// the row is. Empty (a quiet track) until the history arrives.
function Stack({ rows, color, share = 'share', title, className = '' }) {
  return (
    <div className={`ox-stack ${className}`} role="img"
         aria-label={rows ? rows.filter((r) => r[share] > 0).map((r) => `${r.label} ${Math.round(r[share] * 100)}%`).join(', ') : 'loading'}>
      {rows?.filter((r) => r[share] > 0).map((r) => (
        <span key={r.id} className="ox-stack__seg"
              style={{ flexGrow: r[share], background: color(r.id) }}
              title={title ? title(r) : `${r.label}: ${Math.round(r[share] * 100)}%`} />
      ))}
    </div>
  );
}

// The running total, day by day: the shape of the whole effort under the
// number it adds up to.
function Climb({ activity }) {
  if (!activity) return <div className="ox-climb" />;
  const values = activity.cumulative;
  const max = values[values.length - 1] || 1;
  const w = 300, h = 90;
  const pts = values.map((v, i) => [(i / (values.length - 1)) * w, h - (v / max) * (h - 4)]);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  return (
    <svg className="ox-climb" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label="Commits, running total by day">
      <defs>
        <linearGradient id="ox-climb-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="var(--ox-ember)" stopOpacity="0.45" />
          <stop offset="1" stopColor="var(--ox-rust)" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ox-climb-line" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="var(--ox-rust)" />
          <stop offset="1" stopColor="var(--ox-ember)" />
        </linearGradient>
      </defs>
      <path d={`${line} L${w} ${h} L0 ${h} Z`} fill="url(#ox-climb-fill)" />
      <path d={line} fill="none" stroke="url(#ox-climb-line)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// One column per day of the project's life, heat by that day's commits.
function DayBars({ activity, className = '' }) {
  if (!activity) return <div className={`ox-days ${className}`} />;
  const max = Math.max(...activity.perDay) || 1;
  return (
    <div className={`ox-days ${className}`} role="img" aria-label="Commits per day, first day to last">
      {activity.perDay.map((v, i) => (
        <span key={activity.days[i]} className="ox-days__bar"
              style={{ height: `${Math.max(4, (v / max) * 100)}%`, background: heatOf(v, max) ?? 'var(--ox-line-2)' }}
              title={`${activity.days[i]}: ${v.toLocaleString('en-US')} commits`} />
      ))}
    </div>
  );
}

function HeatKey() {
  return (
    <span className="ox-heatkey" aria-hidden="true">
      quiet {[1, 2, 3, 4, 5].map((i) => <i key={i} style={{ background: `var(--heat-${i})` }} />)} busy
    </span>
  );
}

// The wordmark corrodes when you touch it. It is the smallest possible way of
// telling the reader that things on this page respond.
function Wordmark() {
  const [oxide, corrodeOxide] = useScramble('OXIDE');
  const [os, corrodeOs] = useScramble('OS');
  return (
    <h1 className="ox-title" onPointerEnter={() => { corrodeOxide(); corrodeOs(); }}>
      <span className="ox-title__word">{oxide}</span>
      <span className="ox-title__os">{os}</span>
    </h1>
  );
}

function Landing() {
  const page = useRef(null);
  const navigate = useNavigate();
  const progress = useScrollProgress();
  const [neon, setNeon] = useState(false);
  const [panic, setPanic] = useState(null);
  const systems = useKernelData('crates');
  const activity = useKernelData('activity');
  const table = useKernelData('syscalls');

  useReveal(page);
  useSpotlight(page, '.ox-card, .ox-arch__cell');
  useKonami(useCallback(() => setPanic('konami sequence on an unprivileged tty'), []));

  // The shell's `neon` command flips the same switch the rail button does, and
  // has to report which way it went so it can say so in the transcript.
  const toggleNeon = useCallback(() => { setNeon((on) => !on); return !neon; }, [neon]);

  // The hero's grid and its two lights lean a few pixels toward the pointer.
  // Written to custom properties rather than to state: the parallax must not
  // re-render the shell on every mouse move.
  const onHeroMove = (event) => {
    if (reducedMotion()) return;
    const box = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width - 0.5;
    const y = (event.clientY - box.top) / box.height - 0.5;
    event.currentTarget.style.setProperty('--hx', `${(x * 26).toFixed(1)}px`);
    event.currentTarget.style.setProperty('--hy', `${(y * 18).toFixed(1)}px`);
  };

  return (
    <div className={`ox${neon ? ' ox--neon wx-scheme-dark' : ''}`} ref={page}>
      {/* How far down you are, and the only two controls that never leave. */}
      <div className="ox-rail" style={{ '--progress': progress }}>
        <div className="ox-rail__fill" />
        <button type="button" className="ox-rail__neon" onClick={() => setNeon((on) => !on)}
          aria-pressed={neon} title="Flip the page to the neon hour">
          <i className="fa-solid fa-bolt" aria-hidden="true" /> {neon ? 'daylight' : 'neon'}
        </button>
      </div>

      {panic && <Panic reason={panic} stats={stats} onDismiss={() => setPanic(null)} />}

      <main className="app-content">

        {/* Hero */}
        <section className="ox-hero" onPointerMove={onHeroMove}>
          <div className="ox-hero__grid" aria-hidden="true" />
          <div className="ox-hero__glow" aria-hidden="true" />
          <div className="ox-hero__quench" aria-hidden="true" />
          <Embers />
          <div className="ox-wrap ox-hero__inner">
            <div className="ox-hero__copy">
              <span className="ox-eyebrow">
                <b className="ox-eyebrow__tag ox-eyebrow__tag--ember">Iteration 4</b>
                <b className="ox-eyebrow__tag ox-eyebrow__tag--rust">Rust</b>
                <b className="ox-eyebrow__tag ox-eyebrow__tag--quench">Linux ABI</b>
              </span>
              <Wordmark />
              <p className="ox-lede">
                A new operating system kernel that <strong>runs Linux programs</strong>.
                Written from scratch in Rust in {stats.days} days, forked from nothing.
              </p>
              <div className="ox-cta">
                <WxLink className="ox-btn ox-btn--primary" href={SOURCE_URL}>
                  <i className="fa-solid fa-code" /> View the source
                </WxLink>
                <Link className="ox-btn ox-btn--ghost" to="/valley">
                  <i className="fa-solid fa-mountain-sun" /> Walk the kernel
                </Link>
                <Link className="ox-btn ox-btn--ghost" to="/growth" onMouseEnter={warmGrowth} onFocus={warmGrowth}>
                  <i className="fa-solid fa-chart-line" /> See it grow
                </Link>
              </div>
              <p className="ox-hero__nudge">
                <i className="fa-solid fa-terminal" aria-hidden="true" />
                That terminal is real. Type in it, or run <code>snake</code>
              </p>
            </div>

            <Shell
              stats={stats}
              onNavigate={navigate}
              onNeon={toggleNeon}
              onPanic={(reason) => setPanic(reason)}
            />
          </div>
        </section>

        {/* Spec strip */}
        <section className="ox-specs">
          <div className="ox-wrap ox-specs__layout">
            <Spec {...LEAD} className="ox-spec--lead" hue="ember">
              <Climb activity={activity} />
              <div className="ox-spec__foot">
                <span>{stats.firstCommit}</span><span>running total</span><span>{stats.lastCommit}</span>
              </div>
            </Spec>
            <div className="ox-specs__side">
              <Spec {...WIDE} className="ox-spec--wide" hue="rust">
                <Stack rows={systems} color={sysColor} share="locShare" className="ox-stack--tall" />
                <ul className="ox-keys">
                  {(systems ?? []).filter((r) => r.locShare > 0).map((r) => (
                    <li key={r.id}><i style={{ background: sysColor(r.id) }} />{r.label}
                      <b>{r.locShare >= 0.01 ? `${Math.round(r.locShare * 100)}%` : '<1%'}</b></li>
                  ))}
                </ul>
              </Spec>
              <div className="ox-specs__minis">
                {MINIS.map((spec) => (
                  <Spec key={spec.label} {...spec} className="ox-spec--mini">
                    {spec.hue === 'sys' && <Stack rows={systems} color={sysColor} share="crateShare" className="ox-stack--rule" />}
                    {spec.hue === 'fam' && <Stack rows={table?.families} color={famColor} className="ox-stack--rule" />}
                    {(spec.hue === 'quench' || spec.hue === 'ember') && <span className="ox-stack ox-stack--rule ox-stack--solid" />}
                  </Spec>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* The syscall table, running past. 368 is a number; this is a surface. */}
        <section className="ox-syscalls" data-reveal>
          <div className="ox-wrap ox-syscalls__head">
            <span className="ox-kicker">Linux ABI</span>
            <h2 className="ox-h2">{n(stats.syscalls)} syscalls, implemented</h2>
            <p className="ox-syscalls__sub">The table, by family. Every name below wears its family&apos;s colour.</p>
            <Stack rows={table?.families} color={famColor} className="ox-stack--tall ox-syscalls__bar" />
            <ul className="ox-keys ox-keys--row">
              {(table?.families ?? []).map((f) => (
                <li key={f.id}><i style={{ background: famColor(f.id) }} />{f.label}</li>
              ))}
            </ul>
          </div>
          <Ticker />
        </section>

        {/* The valley — the thing worth clicking */}
        <section className="ox-valley ox-valley--walk" data-reveal>
          <div className="ox-wrap ox-valley__inner">
            <div className="ox-valley__copy">
              <span className="ox-kicker">Interactive</span>
              <h2 className="ox-h2">Walk through the kernel</h2>
              <p>
                A million lines is hard to picture, so the workspace builds itself into a
                valley you can fly over and walk through. One building per Rust module, its
                shape read off that module&apos;s own source, its address the file&apos;s real path.
              </p>
              <p className="ox-valley__note">
                A settlement&apos;s wall is its crate boundary. Every gate is a dependency, and
                walking through one takes you to the crate it points at. Nothing in it is decoration.
              </p>
              <p>
                The same map reads two other ways, and the valley&apos;s own controls switch between
                them: as a <Link to="/valley#crates">city of crates</Link> you can orbit or walk, and
                as the <Link to="/valley#graph">dependency graph</Link> the whole workspace makes.
              </p>
              <p>
                At the neon hour it turns into a readout. The ground lights up on the county
                palette, every module wears a band in the colour of what it does, and each crate
                throws a column of light as tall as its dependency traffic.
              </p>
              <div className="ox-cta">
                <Link className="ox-btn ox-btn--primary" to="/valley">
                  <i className="fa-solid fa-play" /> Enter the valley
                </Link>
                <Link className="ox-btn ox-btn--ghost" to="/valley#tour">
                  <i className="fa-solid fa-route" /> Take the guided tour
                </Link>
                <Link className="ox-btn ox-btn--ghost" to="/valley#neon">
                  <i className="fa-solid fa-bolt" /> See it at night
                </Link>
              </div>
            </div>
            <ul className="ox-valley__legend">
              <li><b><i className="ox-glyph ox-glyph--county" aria-hidden="true" />County</b><span>workspace system</span></li>
              <li><b><i className="ox-glyph ox-glyph--town" aria-hidden="true" />Town</b><span>one crate</span></li>
              <li><b><i className="ox-glyph ox-glyph--building" aria-hidden="true" />Building</b><span>one Rust module</span></li>
              <li><b><i className="ox-glyph ox-glyph--room" aria-hidden="true" />Room</b><span>one function</span></li>
              <li><b><i className="ox-glyph ox-glyph--window" aria-hidden="true" />Lit window</b><span>a <code>pub</code> function</span></li>
              <li><b><i className="ox-glyph ox-glyph--road" aria-hidden="true" />Road</b><span>a Cargo dependency</span></li>
            </ul>
          </div>
        </section>

        {/* The history — the valley's counterpart: the same tree, over time */}
        <section className="ox-valley ox-valley--flip" data-reveal>
          <div className="ox-wrap ox-valley__inner">
            <div className="ox-valley__copy">
              <span className="ox-kicker">Interactive</span>
              <h2 className="ox-h2">Watch it get built</h2>
              <p>
                The valley is the kernel as it stands today. This is the same tree with the
                time axis put back: {stats.days} days of history replayed one commit at a
                time, across eighteen charts that each answer a different question about how
                a system this size actually arrives.
              </p>
              <p className="ox-valley__note">
                Every number is extracted from the repository itself. No figure on this site
                is typed by hand, and the scrubber reads the same history the charts do.
              </p>
              <p>
                Watch crates overtake each other for size, see which subsystem was being
                worked on in any given week, or find the day a syscall landed. The
                uncomfortable ones are here too: unsafe density, files over the size cap,
                and how long a branch took to land.
              </p>
              <div className="ox-cta">
                <Link className="ox-btn ox-btn--primary" to="/growth" onMouseEnter={warmGrowth} onFocus={warmGrowth}>
                  <i className="fa-solid fa-chart-line" /> Open the dashboard
                </Link>
                <Link className="ox-btn ox-btn--ghost" to="/growth#race" onMouseEnter={warmGrowth} onFocus={warmGrowth}>
                  <i className="fa-solid fa-ranking-star" /> Race the crates
                </Link>
                <Link className="ox-btn ox-btn--ghost" to="/growth#punch" onMouseEnter={warmGrowth} onFocus={warmGrowth}>
                  <i className="fa-solid fa-calendar-day" /> Every single day
                </Link>
              </div>
            </div>
            <ul className="ox-valley__legend">
              <li><b><i className="ox-glyph ox-glyph--race" aria-hidden="true" />Crate race</b><span>who grew fastest</span></li>
              <li><b><i className="ox-glyph ox-glyph--subsys" aria-hidden="true" />Subsystems</b><span>what was worked on, when</span></li>
              <li><b><i className="ox-glyph ox-glyph--fams" aria-hidden="true" />Syscalls</b><span>the ABI filling in</span></li>
              <li><b><i className="ox-glyph ox-glyph--arch" aria-hidden="true" />Arch parity</b><span>both targets, together</span></li>
              <li><b><i className="ox-glyph ox-glyph--unsafe" aria-hidden="true" />Unsafe density</b><span>per thousand lines</span></li>
              <li><b><i className="ox-glyph ox-glyph--land" aria-hidden="true" />Time to land</b><span>branch to <code>main</code></span></li>
            </ul>
          </div>
        </section>

        {/* Capabilities */}
        <section className="ox-section ox-section--alt" data-reveal>
          <div className="ox-wrap">
            <span className="ox-kicker">What it is</span>
            <h2 className="ox-h2">Four things worth knowing</h2>
            <div className="ox-cards">
              {CAPABILITIES.map((c) => (
                <article className={`ox-card ox-card--${c.hue}`} key={c.title}>
                  <div className="ox-card__icon"><Figure kind={c.figure} stats={stats} data={{ systems, activity, table }} /></div>
                  <h3 className="ox-card__title">{c.title}</h3>
                  <p className="ox-card__body">{c.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <Downloads />

        {/* CTA: the close, and the whole life of the project in one strip */}
        <section className="ox-section ox-cta-band" data-reveal>
          <div className="ox-wrap">
            <div className="ox-prose ox-center">
              <span className="ox-kicker">Open source</span>
              <h2 className="ox-h2">Read it yourself</h2>
              <p>Every commit is public. Browse the kernel, or watch {stats.days} days of it replay one commit at a time.</p>
            </div>
            <figure className="ox-life">
              <DayBars activity={activity} className="ox-life__bars" />
              <figcaption className="ox-life__cap">
                <span>{stats.firstCommit}</span>
                <span className="ox-life__what">every day of the repository, by commits <HeatKey /></span>
                <span>{stats.lastCommit}</span>
              </figcaption>
            </figure>
            <div className="ox-cta ox-center">
              <WxLink className="ox-btn ox-btn--primary" href={SOURCE_URL}>
                <i className="fa-solid fa-code" /> watkins-labs/oxide
              </WxLink>
              <Link className="ox-btn ox-btn--ghost" to="/growth" onMouseEnter={warmGrowth} onFocus={warmGrowth}>
                <i className="fa-solid fa-chart-line" /> {n(stats.commits)} commits, charted
              </Link>
            </div>
            <nav className="ox-doors" aria-label="Where to go next">
              <Link className="ox-door ox-door--valley" to="/valley">
                <span className="ox-door__k">Walk</span>
                <b>The valley</b>
                <span>One building per module, one town per crate.</span>
              </Link>
              <Link className="ox-door ox-door--growth" to="/growth" onMouseEnter={warmGrowth} onFocus={warmGrowth}>
                <span className="ox-door__k">Replay</span>
                <b>The dashboard</b>
                <span>The same tree with the time axis put back.</span>
              </Link>
              <a className="ox-door ox-door--dl" href="#download">
                <span className="ox-door__k">Boot</span>
                <b>The ISOs</b>
                <span>Nano, Micro or Lite, for x86_64 or aarch64.</span>
              </a>
            </nav>
          </div>
        </section>

      </main>
    </div>
  );
}

function App() {
  useFonts();
  useEffect(() => {
    initTheme();
    const unsubscribe = onThemeChange(() => {});
    return () => unsubscribe();
  }, []);

  return (
    <ToastProvider>
      <Router>
        <Routes>
          {/* The valley is deliberately chrome-free: it takes the whole viewport
              and carries its own link back to the site. */}
          <Route
            path="/valley"
            element={
              <Suspense fallback={<div className="ox-boot"><div><i /><p>Loading the valley</p><span>fetching the kernel map</span></div></div>}>
                <Valley />
              </Suspense>
            }
          />
          <Route
            path="/growth"
            element={
              <div className="App">
                <MegaMenu />
                <Suspense fallback={<div className="ox-boot"><div><i /><p>Loading the history</p><span>reading every commit</span></div></div>}>
                  <Growth />
                </Suspense>
                <Footer />
                <ToastContainer />
              </div>
            }
          />
          <Route
            path="*"
            element={
              <div className="App">
                <MegaMenu />
                <Landing />
                <Footer />
                <ToastContainer />
              </div>
            }
          />
        </Routes>
      </Router>
    </ToastProvider>
  );
}

export default App;

// The harness renders the landing on its own, without the template shell.
export { Landing };
